import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../redux/store";
import {
  addLeaveAction,
  editLeaveAction,
  fetchLeavesAction,
  deleteLeaveAction,
} from "../redux/actions/leaveTransactionAction";
import { LeaveTransaction } from "../types/LeaveTransaction";
import LeaveForm from "../components/LeaveTransactionForm";
import LeaveList from "../components/LeaveTransactionList";
import Modal from "../components/Modal";
import CollapseCard from "../components/custom/CollapseCard";
import PageContainer from "../components/custom/PageContainer";
import Main from "../components/custom/Main";
import { StoreState } from "../redux/reducers";
import { useToast } from "../components/toast/ToastProvider";
import LeaveTransactionFilter from "../components/LeaveTransactionFilter";

const LeaveTransactionPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();

  const leaveList = useSelector((state: StoreState) => state.leaveTransaction);
  const [selectedLeave, setSelectedLeave] = useState<LeaveTransaction | null>(
    null,
  );
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filteredRequest, setFilteredRequest] = useState<LeaveTransaction[]>(
    [],
  );
  const [filters, setFilters] = useState({
    employeeId: "",
    year: "",
  });

  useEffect(() => {
    setLoading(true);
    dispatch(fetchLeavesAction()).finally(() => setLoading(false));
  }, [dispatch]);

  const handleSave = async (data: LeaveTransaction) => {
    setLoading(true);
    try {
      if (selectedLeave) {
        await dispatch(editLeaveAction(data.id, data));
        toast("Leave updated successfully");
      } else {
        await dispatch(addLeaveAction(data));
        toast("Leave added successfully");
      }
      await dispatch(fetchLeavesAction());
      setSelectedLeave(null);
      setShowModal(false);
    } catch (error) {
      toast("Error saving leave");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this leave?")) {
      setLoading(true);
      try {
        await dispatch(deleteLeaveAction(id));
        toast("Leave deleted successfully");
        await dispatch(fetchLeavesAction());
      } catch (error) {
        toast("Error deleting leave");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (item: LeaveTransaction) => {
    setSelectedLeave(item);
    setShowModal(true);
  };

  //Filter Logic

  useEffect(() => {
    setFilteredRequest(leaveList);
  }, [leaveList]);

  const applyFilters = (newFilters: typeof filters) => {
    setFilters(newFilters);

    const filtered = leaveList.filter((req) => {
      const matchesEmployee =
        !newFilters.employeeId || req.employeeId === newFilters.employeeId;
      const matchesYear =
        !newFilters.year ||
        (req.createTimestamp &&
          new Date(req.createTimestamp).getFullYear().toString() ===
            newFilters.year);

      return matchesEmployee && matchesYear;
    });
    setFilteredRequest(filtered);
  };

  const handleReset = () => {
    setFilters({ employeeId: "", year: "" });
    setFilteredRequest(leaveList);
  };

  return (
    <Main>
      <PageContainer
        title="Leave Transaction"
        onButtonClick={() => setShowModal(true)}
        buttonText={loading ? "Loading..." : "ADD"}
      >
        <Modal
          visible={showModal}
          size="lg"
          onClose={() => {
            setShowModal(false);
            setSelectedLeave(null);
          }}
          headerText={
            selectedLeave ? "Update Leave Transaction" : "Add Leave Transaction"
          }
        >
          <LeaveForm initialData={selectedLeave} onSave={handleSave} />
        </Modal>

        <CollapseCard title="Leave Transaction Filter">
          <LeaveTransactionFilter
            filters={filters}
            onFilterChange={applyFilters}
            onReset={handleReset}
          />
        </CollapseCard>

        <CollapseCard title="Leave Transactions">
          <LeaveList
            list={filteredRequest}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </CollapseCard>
      </PageContainer>
    </Main>
  );
};

export default LeaveTransactionPage;
